import type { RecordView } from '@kit/sync/types/record-view';
import { useMemo } from 'react';
import { toast } from 'sonner';
import { createItem } from '@/entities/item/api/create-item';
import { deleteItem } from '@/entities/item/api/delete-item';
import { saveItem } from '@/entities/item/api/save-item';
import { isMedical } from '@/entities/item/helpers/is-medical';
import type { Item } from '@/entities/item/schemas/item';
import { usePersonName } from '@/entities/session/hooks/use-person-name';
import { AttachmentsField } from '@/entities/shared/attachment/attachments-field';
import { CATEGORY_OPTIONS } from '@/entities/shared/category/category-options';
import { RecordMeta } from '@/entities/shared/record/record-meta';
import { EditorSheet } from '@/shared/components/editor-sheet/editor-sheet';
import { DateField } from '@/shared/components/form/date-field';
import { FormGrid } from '@/shared/components/form/form-grid';
import { FormSection } from '@/shared/components/form/form-section';
import { MoneyField } from '@/shared/components/form/money-field';
import { NumberField } from '@/shared/components/form/number-field';
import { SelectField } from '@/shared/components/form/select-field';
import { TagsField } from '@/shared/components/form/tags-field';
import { TextField } from '@/shared/components/form/text-field';
import { TextareaField } from '@/shared/components/form/textarea-field';
import { useForm } from '@/shared/hooks/use-form';
import { msg } from '@/shared/i18n/msg';
import { useTranslation } from '@/shared/i18n/use-translation';
import { rankSuggestions } from '@/shared/lib/text/rank-suggestions';
import { fromItemForm } from '../helpers/from-item-form';
import { shelfLifeHint } from '../helpers/shelf-life-hint';
import { toItemForm } from '../helpers/to-item-form';

type ItemEditorProps = {
  readonly view: RecordView<Item> | undefined;
  readonly items: readonly RecordView<Item>[];
  readonly onClose: () => void;
};

const UNITS = ['ud', 'L', 'kg', 'g', msg('pack'), msg('box')];

export function ItemEditor({ view, items, onClose }: ItemEditorProps) {
  const { t } = useTranslation();
  const nameOf = usePersonName();
  const form = useForm(toItemForm(view?.data));
  const { values } = form;
  const suggest = useMemo(
    () => ({
      units: [...new Set([...UNITS.map((unit) => t(unit)), ...rankSuggestions(items, (i) => i.data.unit)])],
      locations: rankSuggestions(items, (i) => i.data.location),
      stores: rankSuggestions(items, (i) => i.data.store),
    }),
    [items, t],
  );

  async function save() {
    const saved = await form.submit(async function write() {
      const data = fromItemForm(values, view?.data);
      if (view) await saveItem(view.id, data);
      else await createItem(data);
    });
    if (!saved) return;
    toast.success(view ? t('Product saved') : t('Product added'));
    onClose();
  }

  async function remove() {
    if (!view) return;
    await deleteItem(view.id);
    onClose();
  }

  return (
    <EditorSheet
      title={view ? view.data.name : t('New product')}
      saving={form.saving}
      onClose={onClose}
      onSubmit={save}
      onDelete={view ? remove : undefined}
    >
      <FormSection title={t('Product')}>
        <TextField
          label={t('Name')}
          required
          autoFocus={!view}
          placeholder={t('e.g. Mineral water 5 L')}
          {...form.field('name')}
        />
        <FormGrid>
          <SelectField label={t('Category')} options={CATEGORY_OPTIONS} {...form.field('category')} />
          <TextField label={t('Brand')} {...form.field('brand')} />
        </FormGrid>
        <FormGrid className="grid-cols-3 sm:grid-cols-3">
          <NumberField label={t('Quantity')} {...form.field('quantity')} />
          <TextField label={t('Unit')} suggestions={suggest.units} {...form.field('unit')} />
          <NumberField label={t('Minimum')} hint={t('Warns below this')} {...form.field('min_quantity')} />
        </FormGrid>
        <TextField
          label={t('Location')}
          placeholder={t('Backpack, pantry, car…')}
          suggestions={suggest.locations}
          {...form.field('location')}
        />
        <TagsField label={t('Tags')} {...form.field('tags')} />
      </FormSection>

      <FormSection title={t('Purchase')}>
        <FormGrid>
          <MoneyField label={t('Price paid')} hint={t('For the whole quantity')} {...form.field('price')} />
          <DateField label={t('Purchase date')} {...form.field('purchased_on')} />
        </FormGrid>
        <TextField label={t('Store')} suggestions={suggest.stores} {...form.field('store')} />
        <TextField
          label={t('Product page')}
          type="url"
          inputMode="url"
          placeholder="https://"
          {...form.field('url')}
        />
      </FormSection>

      <FormSection title={t('Expiry and replacement')}>
        <FormGrid>
          <DateField
            label={t('Expires on')}
            hint={t('Unsafe after this date')}
            {...form.field('expires_on')}
          />
          <DateField
            label={t('Best before')}
            hint={t('Quality, not safety')}
            {...form.field('best_before')}
          />
        </FormGrid>
        <FormGrid>
          <DateField
            label={t('Replace or check on')}
            hint={t('Batteries, water, rotation…')}
            {...form.field('replace_on')}
          />
        </FormGrid>
        <NumberField
          label={t('Shelf life (months)')}
          integer
          hint={shelfLifeHint(values.shelf_life_months, values.purchased_on)}
          {...form.field('shelf_life_months')}
        />
      </FormSection>

      {isMedical(values.category) && (
        <FormSection title={t('Medicine')}>
          <FormGrid>
            <TextField
              label={t('Active ingredient')}
              placeholder={t('e.g. Paracetamol')}
              {...form.field('active_ingredient')}
            />
            <TextField label={t('Dose')} placeholder={t('e.g. 1 g every 8 h')} {...form.field('dosage')} />
          </FormGrid>
        </FormSection>
      )}

      <FormSection title={t('Notes and files')}>
        <TextareaField label={t('Notes')} {...form.field('notes')} />
        <AttachmentsField value={values.attachments} onChange={(value) => form.set('attachments', value)} />
      </FormSection>

      {view && <RecordMeta view={view} nameOf={nameOf} />}
    </EditorSheet>
  );
}
